import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Calendar, BarChart3, BookOpen, User as UserIcon, ShieldAlert, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const navItems = [
    { path: "/", label: "Início", icon: Home, testId: "nav-home-btn" },
    { path: "/agenda", label: "Agenda", icon: Calendar, testId: "nav-agenda-btn" },
    { path: "/operacao", label: "Operação", icon: BarChart3, testId: "nav-operation-btn" },
    { path: "/biblioteca", label: "Biblioteca", icon: BookOpen, testId: "nav-library-btn" },
    { path: "/perfil", label: "Perfil", icon: UserIcon, testId: "nav-profile-btn" },
  ];

  if (user?.role === "admin" || user?.role === "platform_owner") {
    navItems.push({
      path: "/admin",
      label: user?.role === "platform_owner" ? "Dono" : "Mentor Admin",
      icon: ShieldAlert,
      testId: "nav-admin-btn",
    });
  }

  return (
    <>
      {/* Desktop Top Header */}
      <header className="hidden md:flex items-center justify-between px-6 py-3 bg-[#0E1015]/90 backdrop-blur-md border-b border-[#282D3D] sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-gradient-to-br from-[#FF6B00] to-[#E05000] flex items-center justify-center font-extrabold text-black text-lg">
            C
          </div>
          <div className="flex flex-col">
            <span className="font-extrabold text-lg tracking-tight text-white leading-none">
              MODO <span className="text-[#FF6B00]">CAVERNA</span>
            </span>
            <span className="text-[10px] text-gray-400 font-medium">SISTEMA OPERACIONAL DE ESCALA</span>
          </div>
        </Link>

        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[#FF6B00]/15 text-[#FF6B00] border border-[#FF6B00]/30"
                    : "text-gray-300 hover:text-white hover:bg-[#1A1D26]"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs font-semibold text-white">{user?.name}</div>
            <div className="text-[11px] text-[#FF6B00] capitalize">
              {user?.role === "platform_owner" ? "Dono" : user?.role === "admin" ? "Mentor" : "Aluno"}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="p-2 text-gray-400 hover:text-red-400 hover:bg-[#1A1D26] rounded-md transition-colors"
            title="Sair"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Mobile Bottom Bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0E1015]/95 backdrop-blur-xl border-t border-[#282D3D] px-2 pt-2 safe-bottom-nav flex justify-around items-center"
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`mobile-${item.testId}`}
              className={`flex flex-col items-center justify-center py-1 px-2 rounded-lg transition-all ${
                isActive ? "text-[#FF6B00]" : "text-gray-400 hover:text-gray-200"
              }`}
            >
              <Icon className={`w-5 h-5 ${isActive ? "stroke-[2.5]" : "stroke-2"}`} />
              <span className="text-[10px] font-medium mt-0.5">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}
